import { VStack, HStack, Image, useColorMode, useColorModePreference, Box } from '@chakra-ui/react';
import { appContext } from 'App';
import { Typography } from 'Tokens';
import { useContext, useEffect } from 'react';
import { useLocalStorage } from 'react-use';

enum ColorModesEnum {
  dark = 'dark',
  light = 'light',
  system = 'system',
  new = 'new',
}

const colorModes = {
  [ColorModesEnum.light]: {
    name: 'Light',
    image: '/assets/illustration_light_mode.svg',
    colorMode: ColorModesEnum.light,
  },
  [ColorModesEnum.dark]: {
    name: 'Dark',
    image: '/assets/illustration_dark_mode.svg',
    colorMode: ColorModesEnum.dark,
  },
  [ColorModesEnum.system]: {
    name: 'System',
    image: '/assets/illustration_system_mode.svg',
    colorMode: ColorModesEnum.system,
  },
  [ColorModesEnum.new]: {
    name: 'New',
    image: '/assets/illustration_new_mode.svg',
    colorMode: ColorModesEnum.new,
  },
};

const ModeCard = ({
  image,
  name,
  selected,
  toggle,
}: {
  image: string;
  name: string;
  selected: boolean;
  toggle: () => void;
}) => {
  return (
    <VStack alignItems="start" gap="6px" cursor="pointer" onClick={toggle}>
      <Box
        outline={selected ? '2px solid' : 'none'}
        outlineColor={selected ? 'border.selected.accent' : ''}
        borderRadius="6px"
        overflow="hidden"
        transition="all 0.2s"
      >
        <Image
          src={image}
          transition="all 0.2s ease-in-out"
          _hover={{
            transform: 'scale(1.1)',
          }}
        />
      </Box>
      <Typography
        variant="bodyStrong"
        color={selected ? 'text.selected' : 'text.default'}
        transition="all 0.2s"
      >
        {name}
      </Typography>
    </VStack>
  );
};

export const DarkModeSwitch = () => {
  const { setColorMode, colorMode } = useColorMode();
  const systemColorMode = useColorModePreference();
  const [selectedMode, setSelectedMode] = useLocalStorage(
    'current-color-mode',
    ColorModesEnum.light
  );

  const { usedColorMode, setUsedColorMode } = useContext(appContext);

  useEffect(() => {
    if (selectedMode === ColorModesEnum.system) {
      setColorMode(systemColorMode);
      setUsedColorMode(systemColorMode ?? '');
    } else {
      setUsedColorMode(selectedMode || usedColorMode);
    }
  }, [selectedMode, usedColorMode, systemColorMode]);

  return (
    <HStack justifyContent="space-between" padding="8px" gap="6px">
      {Object.values(colorModes).map((mode) => (
        <ModeCard
          key={mode.colorMode}
          image={mode.image}
          name={mode.name}
          selected={selectedMode === mode.colorMode}
          toggle={() => {
            setUsedColorMode(mode.colorMode);
            if (mode.colorMode === ColorModesEnum.light) setColorMode('light');
            if (mode.colorMode === ColorModesEnum.dark) setColorMode('dark');
            if (mode.colorMode === ColorModesEnum.new) setColorMode('light');
            setSelectedMode(mode.colorMode);
          }}
        />
      ))}
    </HStack>
  );
};
